import { redactValue } from './google-analytics.pii';

/**
 * Query parameter keys that represent meaningfully different pages in GA4 `page_view` events.
 * Only these keys are reported to GA4; all others are filtered out. This is a curated starting list
 * that can be expanded as needed. Keys are ordered for stable serialization: `?a=1&b=2` and `?b=2&a=1`
 * produce the same report.
 */
const PAGE_QUERY_PARAMS: readonly string[] = ['action', 'maxVersion', 'status', 'worksheetId'];

/** Keys come out in `PAGE_QUERY_PARAMS` order, so `?a=1&b=2` and `?b=2&a=1` are one string. */
function selectRawPageParams(search: string): URLSearchParams {
    const searchParams = new URLSearchParams(search);
    const selected = new URLSearchParams();

    for (const key of PAGE_QUERY_PARAMS) {
        for (const value of searchParams.getAll(key)) {
            if (value !== '') {
                selected.append(key, value);
            }
        }
    }

    return selected;
}

/**
 * Builds a GA4 `page_location` from origin, route pattern, and filtered query params.
 *
 * Selects only PAGE_QUERY_PARAMS keys, redacts PII, and assembles into a reportable URL.
 * The path is a declared route pattern (e.g., `/qmrs/:id`, never `/qmrs/abc-123`).
 *
 * @param origin - the app's origin
 * @param path - the resolved route pattern
 * @param search - the query string, with or without leading `?`
 * @returns a GA4-safe URL with only reportable, redacted params
 */
export function buildPageUrl(origin: string, path: string, search: string): string {
    const reported = new URLSearchParams();

    for (const [key, value] of selectRawPageParams(search)) {
        // Redaction can empty a value outright: a whitespace-only or zero-width one normalises away to nothing.
        const redacted = redactValue(value);
        if (redacted !== '') {
            reported.append(key, redacted);
        }
    }

    const query = reported.toString();
    if (!query) {
        return `${origin}${path}`;
    }

    return `${origin}${path}?${query}`;
}

/**
 * Values stay raw: two worksheets whose ids both redact to `(redacted:uuid)` are two pages; if redacted values were
 * used for deduplication, the second navigation could be suppressed as a repeat. Safe because this string is compared
 * in memory and never sent.
 */
export function selectPageChangeParams(search: string): string {
    return selectRawPageParams(search).toString();
}
